<template>
  <v-dialog v-model="activator"
            :fullscreen="isFullscreen"
            :scrim="!fullscreen"
            :persistent="persistent"
            :width="dialogWidth"
            :aria-labelledby="ariaLabelledby"
            v-bind="attrs.componentAttrs"
  >
    <slot name="default">
      <v-card class="app-modal" :class="{ 'app-modal--fullscreen': fullscreen }" :data-locator="attrs.dataLocator">
        <div v-if="!hideHeader" class="app-modal__header" :class="{ 'app-modal__header--with-tabs': hasTabs }">
          <h2 :id="titleId" class="app-modal__title text-text-primary text-truncate" data-locator="dialog-title">
            <!-- @slot for title content -->
            <slot name="title"></slot>
          </h2>
          <div class="app-modal__header-actions" :class="{ 'app-modal__header-actions--fullscreen': fullscreen }">
            <!-- @slot for an optional header action (button or other) -->
            <div v-if="$slots['header-action']" data-locator="modal-header-action">
              <slot name="header-action"></slot>
            </div>
            <!-- In fullscreen mode, the cancel/ok actions live in the header -->
            <div v-if="showHeaderActions" class="app-modal__header-buttons">
              <app-button
                v-if="!okOnly"
                type="ghost"
                variant="neutral"
                :disabled="cancelDisabled"
                :loading="cancelLoading"
                data-locator="cancel-button"
                @click="cancel"
              >
                {{ computedCancelText }}
              </app-button>
              <app-button
                v-if="!cancelOnly"
                type="primary"
                variant="brand"
                :disabled="okDisabled"
                :loading="okLoading"
                data-locator="confirm-button"
                @click="ok"
              >
                {{ computedOkText }}
              </app-button>
            </div>
            <div v-if="showHeaderActions && closeButton" class="app-modal__header-divider"></div>
            <app-button
              v-if="closeButton"
              type="icon"
              class="overlay-close-button mt-n1"
              data-locator="close-button"
              :aria-label="$t('common.close')"
              :disabled="cancelDisabled"
              size="small"
              @click="cancel"
            >
              <template #left-icon>
                <app-material-icon name="close_small" size="lg" />
              </template>
            </app-button>
          </div>
        </div>

        <v-tabs
          v-if="hasTabs"
          v-model="activeTab"
          class="app-modal__tabs"
        >
          <v-tab
            v-for="tabItem in tabs"
            :key="tabItem.value"
            :value="tabItem.value"
            :data-locator="`${tabItem.value}-tab`"
            :ripple="false"
          >
            {{ tabItem.label }}
          </v-tab>
        </v-tabs>

        <v-card-text data-locator="dialog-content">
          <!-- With tabs: content is driven by the active tab through per-tab slots -->
          <v-tabs-window v-if="hasTabs" v-model="activeTab">
            <v-tabs-window-item
              v-for="tabItem in tabs"
              :key="tabItem.value"
              :value="tabItem.value"
              :data-locator="`${tabItem.value}-tab-content`"
            >
              <!-- @slot dynamic per-tab content, named `tab-<value>` -->
              <slot :name="`tab-${tabItem.value}`"></slot>
            </v-tabs-window-item>
          </v-tabs-window>
          <!-- @slot for body content (used when there are no tabs) -->
          <slot v-else name="body"></slot>
        </v-card-text>

        <v-card-actions v-if="!hideFooter && !$slots['footer'] && !fullscreen" class="app-modal__actions">
          <!-- @slot for an optional footer link, aligned to the left -->
          <div v-if="$slots['footer-link']" data-locator="modal-footer-link">
            <slot name="footer-link"></slot>
          </div>
          <v-spacer></v-spacer>
          <app-button
            v-if="!okOnly"
            type="ghost"
            variant="neutral"
            :disabled="cancelDisabled"
            :loading="cancelLoading"
            data-locator="cancel-button"
            @click="cancel"
          >
            {{ computedCancelText }}
          </app-button>
          <app-button
            v-if="!cancelOnly"
            type="primary"
            variant="brand"
            :disabled="okDisabled"
            :loading="okLoading"
            data-locator="confirm-button"
            @click="ok"
          >
            {{ computedOkText }}
          </app-button>
        </v-card-actions>
        <v-card-actions v-if="$slots['footer']" class="app-modal__actions">
          <!-- @slot for a fully custom footer -->
          <slot name="footer"></slot>
        </v-card-actions>
      </v-card>
    </slot>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, useId, type PropType } from 'vue'

import { useDataLocator } from '@/common/utils/useDataLocator'
import AppButton from '@/design-system/buttons/AppButton.vue'
import AppMaterialIcon from '@/design-system/icons/AppMaterialIcon.vue'

export interface AppModalTab {
  value: string | number
  label: string
}

export type AppModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const MODAL_SIZES = Object.freeze({
  xs: '400',
  sm: '460',
  md: '640',
  lg: '800',
  xl: '960',
})

/**
 * Design-system modal based on Vuetify v-dialog.
 * Uses the standard v-model contract (modelValue / update:modelValue).
 * @displayName Modal
 */
export default defineComponent({
  name: 'AppModal',
  components: {
    AppButton,
    AppMaterialIcon,
  },
  inheritAttrs: false,
  setup () {
    const { attrs } = useDataLocator()
    // Names the dialog for assistive technologies, since role="dialog" alone carries no
    // accessible name. Only referenced when the title is actually rendered: see ariaLabelledby.
    const titleId = useId()
    return { attrs, titleId }
  },
  props: {
    /**
     * @model
     * Visibility state of the modal
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * Width of the modal
     * @values xs (400px), sm (460px), md (640px, default), lg (800px), xl (960px)
     */
    size: {
      type: String as PropType<AppModalSize>,
      validator: (value: string) => Object.keys(MODAL_SIZES).includes(value),
      default: 'md',
    },
    /**
     * Neither clicking outside of the element nor pressing Esc will deactivate it.
     * Defaulting to true is a deliberate divergence from the design-system spec, whose
     * keyboard table has Esc close the modal: decided with design, closing stays explicit
     * through the close button or the cancel action.
     */
    persistent: {
      type: Boolean,
      default: true,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    /**
     * If the close button is displayed
     */
    closeButton: {
      type: Boolean,
      default: true,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional tabs displayed under the header
     */
    tabs: {
      type: Array as PropType<AppModalTab[]>,
      default: () => [],
    },
    /**
     * @model
     * Active tab value (use with v-model:tab)
     */
    tab: {
      type: [String, Number] as PropType<string | number | undefined>,
      default: undefined,
    },
    /**
     * Text displayed inside the cancel button
     */
    cancelText: {
      type: String,
      default: undefined,
    },
    /**
     * Set loading state of the cancel button
     */
    cancelLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Removes the ability to click or target the cancel button
     */
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Ok button will not be displayed if set to true
     */
    cancelOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Cancel button will not be displayed if set to true
     */
    okOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Text displayed inside the ok button
     */
    okText: {
      type: String,
      default: undefined,
    },
    /**
     * Set loading state of the ok button
     */
    okLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Removes the ability to click or target the ok button
     */
    okDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:tab', 'cancel', 'ok'],
  data () {
    return {
      internalTab: undefined as string | number | undefined,
      elementFocusedBeforeOpening: null as HTMLElement | null,
    }
  },
  watch: {
    /**
     * Restores keyboard focus to whatever triggered the modal once it closes.
     * Vuetify only refocuses its own `activator`, which almost none of our modals use
     * (they are driven by v-model from an outside button), so without this the focus
     * falls back to <body> and tab navigation restarts from the top of the page.
     */
    modelValue (isOpen: boolean) {
      if (isOpen) {
        const activeElement = document.activeElement
        this.elementFocusedBeforeOpening = activeElement instanceof HTMLElement ? activeElement : null
        return
      }

      const elementToRefocus = this.elementFocusedBeforeOpening
      this.elementFocusedBeforeOpening = null
      // The trigger may have been removed meanwhile (a menu item, a deleted row...)
      if (!elementToRefocus?.isConnected) return

      this.$nextTick(() => elementToRefocus.focus({ preventScroll: true }))
    },
  },
  computed: {
    activator: {
      get (): boolean {
        return this.modelValue
      },
      set (value: boolean) {
        this.$emit('update:modelValue', value)
      },
    },
    activeTab: {
      get () {
        return this.tab ?? this.internalTab ?? this.tabs[0]?.value
      },
      set (value: string | number) {
        this.internalTab = value
        this.$emit('update:tab', value)
      },
    },
    hasTabs (): boolean {
      return this.tabs.length > 0
    },
    /**
     * Points at the title only when that title is really in the DOM, otherwise the dialog
     * would carry a dangling or empty reference and end up with no accessible name at all.
     * Three ways to lose it: a `default` slot replaces the whole card, header included;
     * `hideHeader` drops the header; no `title` slot leaves the heading empty.
     * Left undefined in those cases so a consumer can still pass its own aria-label.
     */
    ariaLabelledby (): string | undefined {
      const rendersTitle = !this.$slots['default'] && !this.hideHeader && !!this.$slots['title']
      return rendersTitle ? this.titleId : undefined
    },
    /**
     * In fullscreen mode the default cancel/ok actions are relocated into the header.
     * A custom `footer` slot opts out of this relocation.
     */
    showHeaderActions (): boolean {
      return this.fullscreen && !this.hideFooter && !this.$slots['footer']
    },
    computedCancelText (): string {
      return this.cancelText ?? this.$t('common.cancel')
    },
    computedOkText (): string {
      return this.okText ?? this.$t('common.validate')
    },
    /**
     * Forces fullscreen when explicitly requested or when the viewport is md-and-down,
     * so the sized modals (xs..xl) never overflow narrow screens.
     */
    isFullscreen (): boolean {
      return this.fullscreen || this.$vuetify.display.mdAndDown
    },
    dialogWidth (): string | undefined {
      return this.isFullscreen ? undefined : MODAL_SIZES[this.size]
    },
  },
  methods: {
    /**
     * Gets called when the user clicks the cancel or close button
     * @event cancel
     */
    cancel () {
      if (!this.cancelDisabled) {
        this.$emit('cancel')
      }
    },
    /**
     * Gets called when the user clicks the ok button
     * @event ok
     */
    ok () {
      if (!this.okLoading && !this.okDisabled) {
        this.$emit('ok')
      }
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

@import "vuetify/settings";
@import "@/scss/typography-tokens";

.app-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 720px;
  overflow: hidden;
  border: get-border();

  &--fullscreen {
    max-height: none;
    height: 100%;
    border: none;
    @include border-radius('border-radius-0');
    box-shadow: none !important;

    // Only reachable through a custom footer slot: fullscreen relocates the default
    // cancel/ok actions into the header, see showHeaderActions.
    .app-modal__actions {
      border-top: get-border('border-secondary');
    }
  }

  &__header {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    column-gap: 20px;
    padding: 24px 24px 20px;

    &--with-tabs {
      padding-bottom: 8px;
    }
  }

  &__title {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    column-gap: 8px;

    &--fullscreen {
      column-gap: 16px;
    }
  }

  &__header-buttons {
    display: flex;
    align-items: center;
    column-gap: 16px;
  }

  &__header-divider {
    width: 1px;
    height: 20px;
    background-color: rgb(var(--v-theme-grey-200));
  }

  &__tabs {
    padding: 0 24px;
    border-bottom: get-border('border-secondary');

    :deep(.v-tab) {
      @include text ('heading-xs-bold');
      color: rgb(var(--v-theme-text-tertiary));
    }

    :deep(.v-tab__slider) {
      bottom: 1px;
      background: rgb(var(--v-theme-primary));
    }
  }

  .v-card-text {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 4px 24px !important;
    color: rgb(var(--v-theme-grey-800));
    letter-spacing: normal !important;
    line-height: normal !important;
  }

  &__actions {
    bottom: 0;
    width: 100%;
    background-color: rgb(var(--v-theme-white));
    padding: 20px 24px 24px;
    column-gap: 16px;
  }
}

@media #{map-get($display-breakpoints, 'xs')} {
  .app-modal {
    &__header {
      padding: 12px 12px 8px !important;
    }

    &__tabs {
      padding: 0 12px !important;
    }

    .v-card-text {
      padding: 4px 12px !important;
    }

    &__actions {
      padding: 8px 12px 12px !important;
    }
  }
}
</style>
